import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Body2, List, ListItem, ListItemText, Card } from '@dhl-ui/components'
import type { ProfileAddress } from '@dhl/receiver-profile-api'
import { type EnrichedDeliveryPreference } from '~/profile/view/preferences/model/model.js'
import { DeliveryPreferenceDescription } from '~/profile/view/preferences/components/DeliveryPreferenceDescription.js'

type Props = {
  firstName?: string
  lastName?: string
  preference?: EnrichedDeliveryPreference
  address?: ProfileAddress
  primaryEmailAddress?: string
  additionalEmailAddress?: string
  mobileNumber?: string
  notificationPreferences: {
    emailNotificationsEnabled: boolean
    pushNotificationsForIncomingParcelsEnabled: boolean
    pushNotificationsForOutgoingParcelsEnabled: boolean
  }
}

type NameValuePair = {
  title: string
  value?: string | undefined | React.ReactNode
}

export const ProfileSummary: FC<Props> = ({
  additionalEmailAddress,
  address,
  firstName,
  lastName,
  mobileNumber,
  notificationPreferences: {
    emailNotificationsEnabled,
    pushNotificationsForIncomingParcelsEnabled,
    pushNotificationsForOutgoingParcelsEnabled,
  },
  preference,
  primaryEmailAddress,
}) => {
  const { t } = useTranslation()

  const getFormattedAddress = (profileAddress?: ProfileAddress) => {
    if (!profileAddress) {
      return undefined
    }

    const { address: addressToFormat } = profileAddress

    return `${addressToFormat.street} ${addressToFormat.houseNumber}${addressToFormat.houseNumberSuffix}, ${addressToFormat.postalCode} ${addressToFormat.city}`
  }

  const getFormattedDeliveryPreference = (preferenceValue?: EnrichedDeliveryPreference) => {
    if (!preferenceValue) {
      return undefined
    }

    return <DeliveryPreferenceDescription preference={preferenceValue} />
  }

  const getFormattedNotificationPreferences = () => {
    const items: string[] = []

    if (pushNotificationsForIncomingParcelsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.pushNotificationsCardTitle')}: ${t('portal.profileSetup.notificationPreference.pushNotificationIncomingParcelsLabel')}`
      )
    }
    if (pushNotificationsForOutgoingParcelsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.pushNotificationsCardTitle')}: ${t('portal.profileSetup.notificationPreference.pushNotificationOutgoingParcelsLabel')}`
      )
    }
    if (emailNotificationsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.emailNotificationCardTitle')}: ${t('portal.profileSetup.notificationPreference.emailNotificationLabel')}`
      )
    }

    if (items.length === 0) {
      return undefined
    }

    return (
      <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Box>
    )
  }

  const getProfileData: () => NameValuePair[] = () => {
    const emailAddresses = [primaryEmailAddress, additionalEmailAddress].filter(Boolean).join(', ')
    const name = [firstName, lastName].filter(Boolean).join(' ')

    return [
      { title: t('portal.profileSetup.done.labels.name'), value: name },
      { title: t('portal.profileSetup.done.labels.address'), value: getFormattedAddress(address) },
      {
        title: t('portal.profileSetup.done.labels.deliveryPreference'),
        value: getFormattedDeliveryPreference(preference),
      },
      { title: t('portal.profileSetup.done.labels.emailAddresses'), value: emailAddresses },
      { title: t('portal.profileSetup.done.labels.phoneNumber'), value: mobileNumber },
      { title: t('portal.profileSetup.done.labels.notifications'), value: getFormattedNotificationPreferences() },
    ].filter(({ value }) => !!value)
  }

  const profileData = getProfileData()

  if (!profileData.length) {
    return null
  }

  return (
    <Card hasPaddingTop>
      <Card.Header title={t('portal.profileSetup.done.cardTitle')} />
      <Card.Content noPaddingBottom noPaddingSides noPaddingTop>
        <List>
          {profileData.map(({ title, value }, index) => (
            // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
            <ListItem hasDivider={index !== profileData.length - 1} key={index}>
              <ListItemText emphasis primary={title}>
                <Body2 sx={{ pt: 0.25 }}>{value}</Body2>
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Card.Content>
    </Card>
  )
}
